import * as React from 'react'

import {
  ActionButton,
  FieldLabel,
  FieldTitle,
  labelize,
  SectionHelpText,
  sectionStyle,
  SectionTitle,
  SelectInput,
  TextInput,
  Toggle,
} from './puck-config-field-ui'

import {
  type FooterConfig,
  FOOTER_LAYOUT_PRESETS,
  FOOTER_SPACING_OPTIONS,
  FOOTER_WIDTH_OPTIONS,
} from '@/types/header-footer-config'
import type { FooterItem } from '@/types/nav.type'

interface FooterConfigFieldProps {
  value: unknown
  onChange: (value: unknown) => void
  name?: string
}

function toFooterConfig(value: unknown): FooterConfig {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  return value as FooterConfig
}

function asGroups(value: FooterItem[] | undefined): FooterItem[] {
  return Array.isArray(value) ? value : []
}

function createDefaultFooterLink() {
  return { title: 'New link', href: '/', external: false }
}

function createDefaultFooterGroup(): FooterItem {
  return { title: 'New group', items: [createDefaultFooterLink()] }
}

export function FooterConfigFieldRender({ value, onChange, name }: FooterConfigFieldProps) {
  const [draft, setDraft] = React.useState<FooterConfig>(() => toFooterConfig(value))

  React.useEffect(() => setDraft(toFooterConfig(value)), [value])

  const commit = (updater: (prev: FooterConfig) => FooterConfig) => {
    setDraft(prev => {
      const next = updater(prev)
      onChange(next)
      return next
    })
  }

  const linkGroups = asGroups(draft.linkGroups)

  const updateLinkGroups = (updater: (prev: FooterItem[]) => FooterItem[]) => {
    commit(prev => ({ ...prev, linkGroups: updater(asGroups(prev.linkGroups)) }))
  }

  return (
    <div>
      <FieldTitle title={labelize(name, 'Footer Config')} />

      <div style={sectionStyle}>
        <SectionTitle title="Layout" />
        <SectionHelpText text="Control footer structure, spacing, and content width." />
        <div style={{ display: 'grid', gap: '6px', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
          <label>
            <FieldLabel label="Layout preset" />
            <SelectInput
              value={draft.layoutPreset || 'default'}
              onChange={event =>
                commit(prev => ({ ...prev, layoutPreset: event.target.value as FooterConfig['layoutPreset'] }))
              }
            >
              {FOOTER_LAYOUT_PRESETS.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </SelectInput>
          </label>
          <label>
            <FieldLabel label="Container width" />
            <SelectInput
              value={draft.containerWidth || 'content'}
              onChange={event =>
                commit(prev => ({ ...prev, containerWidth: event.target.value as FooterConfig['containerWidth'] }))
              }
            >
              {FOOTER_WIDTH_OPTIONS.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </SelectInput>
          </label>
          <label>
            <FieldLabel label="Spacing" />
            <SelectInput
              value={draft.spacing || 'normal'}
              onChange={event => commit(prev => ({ ...prev, spacing: event.target.value as FooterConfig['spacing'] }))}
            >
              {FOOTER_SPACING_OPTIONS.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </SelectInput>
          </label>
        </div>
      </div>

      <div style={sectionStyle}>
        <SectionTitle title="Visibility" />
        <SectionHelpText text="Choose which footer elements are shown." />
        <div style={{ display: 'grid', gap: '6px' }}>
          <Toggle
            checked={(draft.showLogo ?? false) === true}
            label="Show logo"
            onChange={next => commit(prev => ({ ...prev, showLogo: next }))}
          />
          <Toggle
            checked={draft.showLinkGroups === true}
            label="Show link groups"
            onChange={next => commit(prev => ({ ...prev, showLinkGroups: next }))}
          />
          <Toggle
            checked={draft.showSocialLinks !== false}
            label="Show social links"
            onChange={next => commit(prev => ({ ...prev, showSocialLinks: next }))}
          />
          <SectionHelpText text="Icons use URLs from Admin → Default media. Add or edit platforms there." />
          <Toggle
            checked={draft.showCopyright !== false}
            label="Show copyright"
            onChange={next => commit(prev => ({ ...prev, showCopyright: next }))}
          />
          <Toggle
            checked={draft.showBorder !== false}
            label="Show border"
            onChange={next => commit(prev => ({ ...prev, showBorder: next }))}
          />
        </div>
      </div>

      <div style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <SectionTitle title="Link groups" />
          </div>
          <ActionButton onClick={() => updateLinkGroups(prev => [...prev, createDefaultFooterGroup()])}>
            Add group
          </ActionButton>
        </div>

        {linkGroups.length === 0 && (
          <SectionHelpText text="No custom groups yet. Add group to seed an editable starter group and link." />
        )}

        {linkGroups.map((group, groupIndex) => (
          <div key={`group-${groupIndex}`} style={sectionStyle}>
            <label>
              <FieldLabel label="Group title" />
              <TextInput
                placeholder="Explore"
                value={group.title || ''}
                onChange={event =>
                  updateLinkGroups(prev =>
                    prev.map((currentGroup, currentGroupIndex) =>
                      currentGroupIndex === groupIndex ? { ...currentGroup, title: event.target.value } : currentGroup
                    )
                  )
                }
              />
            </label>

            <ActionButton
              onClick={() =>
                updateLinkGroups(prev =>
                  prev.map((currentGroup, currentGroupIndex) =>
                    currentGroupIndex === groupIndex
                      ? {
                          ...currentGroup,
                          items: [...(currentGroup.items || []), createDefaultFooterLink()],
                        }
                      : currentGroup
                  )
                )
              }
            >
              Add link
            </ActionButton>

            {(group.items || []).map((item, itemIndex) => (
              <div key={`group-${groupIndex}-item-${itemIndex}`} style={sectionStyle}>
                <label>
                  <FieldLabel label="Link title" />
                  <TextInput
                    placeholder="Learning path"
                    value={item.title || ''}
                    onChange={event =>
                      updateLinkGroups(prev =>
                        prev.map((currentGroup, currentGroupIndex) =>
                          currentGroupIndex === groupIndex
                            ? {
                                ...currentGroup,
                                items: (currentGroup.items || []).map((link, linkIndex) =>
                                  linkIndex === itemIndex ? { ...link, title: event.target.value } : link
                                ),
                              }
                            : currentGroup
                        )
                      )
                    }
                  />
                </label>
                <label>
                  <FieldLabel label="Link href" />
                  <TextInput
                    placeholder="/learning-path"
                    value={item.href || ''}
                    onChange={event =>
                      updateLinkGroups(prev =>
                        prev.map((currentGroup, currentGroupIndex) =>
                          currentGroupIndex === groupIndex
                            ? {
                                ...currentGroup,
                                items: (currentGroup.items || []).map((link, linkIndex) =>
                                  linkIndex === itemIndex ? { ...link, href: event.target.value } : link
                                ),
                              }
                            : currentGroup
                        )
                      )
                    }
                  />
                </label>
                <Toggle
                  checked={item.external === true}
                  label="Open in new tab"
                  onChange={next =>
                    updateLinkGroups(prev =>
                      prev.map((currentGroup, currentGroupIndex) =>
                        currentGroupIndex === groupIndex
                          ? {
                              ...currentGroup,
                              items: (currentGroup.items || []).map((link, linkIndex) =>
                                linkIndex === itemIndex ? { ...link, external: next } : link
                              ),
                            }
                          : currentGroup
                      )
                    )
                  }
                />
                <ActionButton
                  onClick={() =>
                    updateLinkGroups(prev =>
                      prev.map((currentGroup, currentGroupIndex) =>
                        currentGroupIndex === groupIndex
                          ? { ...currentGroup, items: (currentGroup.items || []).filter((_, i) => i !== itemIndex) }
                          : currentGroup
                      )
                    )
                  }
                >
                  Remove link
                </ActionButton>
              </div>
            ))}

            <ActionButton onClick={() => updateLinkGroups(prev => prev.filter((_, i) => i !== groupIndex))}>
              Remove group
            </ActionButton>
          </div>
        ))}
      </div>
    </div>
  )
}
